import { useState, useRef, useEffect, useMemo } from "react";
import { RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer } from "recharts";
import { AlertTriangle, Shield, Database, Scale, Activity } from "lucide-react";
import { AnimatePresence } from "framer-motion";
import { useIsMobile } from "@/hooks/use-mobile";
import AnimatedTooltip from "./AnimatedTooltip";
import type { CityData } from "@/utils/cityData";

interface InteractiveRiskMapProps {
  city: CityData;
}

const InteractiveRiskMap = ({ city }: InteractiveRiskMapProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const [activeRisk, setActiveRisk] = useState<string | null>(null);
  const [selectedRisk, setSelectedRisk] = useState<string | null>(null);
  const isMobile = useIsMobile();
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, []);

  const riskData = useMemo(() => [
    { 
      risk: "Ransomware", 
      value: 95, 
      icon: AlertTriangle,
      detail: "Ø 15.000€ Lösegeld + Betriebsausfall",
      cityStats: `${city.stats.cyberIncidents} Vorfälle in ${city.name} 2024`,
      avgDamage: "15.000 €",
      avgDowntime: "3-7 Tage",
      affectedPractices: "28% der Praxen in 2024",
      funFact: "80% zahlen Lösegeld, nur 65% erhalten ihre Daten vollständig zurück"
    },
    { 
      risk: "DSGVO", 
      value: 85,
      icon: Scale,
      detail: "Bußgelder bis 20 Mio. €",
      cityStats: "42% der Praxen betroffen",
      avgDamage: "50.000 €",
      avgDowntime: "2-4 Wochen",
      affectedPractices: "42% hatten Datenschutzvorfälle",
      funFact: "Kleinste dokumentierte DSGVO-Strafe: 600€, höchste im Gesundheitswesen: 4,25 Mio. €"
    },
    { 
      risk: "Datenverlust", 
      value: 80,
      icon: Database,
      detail: "Patientendaten unwiederbringlich",
      cityStats: "Irreparabler Reputationsschaden",
      avgDamage: "22.000 €",
      avgDowntime: "5-14 Tage",
      affectedPractices: "18% verloren kritische Daten",
      funFact: "93% der Unternehmen ohne Backup gehen innerhalb eines Jahres insolvent"
    },
    { 
      risk: "Forensik", 
      value: 75,
      icon: Activity,
      detail: "IT-Spezialisten vor Ort binnen 2h",
      cityStats: "Ø 8.500€ Forensikkosten",
      avgDamage: "8.500 €",
      avgDowntime: "1-3 Tage",
      affectedPractices: "Jeder Cybervorfall benötigt Analyse",
      funFact: "Forensik-Experten verdienen Ø 450€/Stunde, Einsätze dauern meist 15-25h"
    },
    { 
      risk: "Prävention", 
      value: 90,
      icon: Shield,
      detail: "24/7 Monitoring & Security-Updates",
      cityStats: "98% Angriffsverhinderung",
      avgDamage: "0 €",
      avgDowntime: "0 Tage",
      affectedPractices: "Nur 12% haben professionelle Prävention",
      funFact: "Jeder in Prävention investierte Euro spart durchschnittlich 5€ Schadenkosten"
    },
  ], [city.stats.cyberIncidents, city.name]);

  const activeRiskData = useMemo(() => 
    riskData.find(r => r.risk === activeRisk),
    [activeRisk, riskData]
  );

  // Click outside handler for mobile
  useEffect(() => {
    if (!isMobile) return;
    
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (selectedRisk && !target.closest('.risk-card') && !target.closest('.animated-tooltip')) {
        setSelectedRisk(null);
        setActiveRisk(null);
      }
    };
    
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [isMobile, selectedRisk]);

  const handleRiskInteraction = (risk: string) => {
    if (isMobile) {
      // Mobile: Toggle selection
      const newSelection = selectedRisk === risk ? null : risk;
      setSelectedRisk(newSelection);
      setActiveRisk(newSelection);
    } else {
      // Desktop: Hover
      setActiveRisk(risk);
    }
  };

  return (
    <section ref={ref} className="py-8 sm:py-12 lg:py-16 relative">
      <div className="container mx-auto max-w-6xl px-3 sm:px-4 lg:px-8">
        <div className="text-center mb-8 sm:mb-10 lg:mb-12">
          <h2 className="text-xl sm:text-2xl lg:text-3xl xl:text-4xl font-bold mb-3 sm:mb-4 bg-clip-text text-transparent bg-gradient-to-r from-primary via-accent to-primary">
            Cyber-Risiken in {city.name}
          </h2>
          <p className="text-sm sm:text-base lg:text-lg text-muted-foreground max-w-2xl mx-auto">
            Interaktive Visualisierung der häufigsten Bedrohungen für Praxen
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-6 sm:gap-8 items-center">
          {/* Chart - Visible on all devices */}
          <div className={`relative ${isVisible ? 'animate-chart-enter' : 'opacity-0'}`}>
            {/* Animated Tooltip */}
            <AnimatePresence>
              {activeRiskData && (
                <AnimatedTooltip data={activeRiskData} />
              )}
            </AnimatePresence>

            <ResponsiveContainer width="100%" height={isMobile ? 300 : 420}>
              <RadarChart data={riskData}>
                <defs>
                  <radialGradient id="radarGradient">
                    <stop offset="0%" stopColor="hsl(var(--destructive))" stopOpacity={0.8} />
                    <stop offset="100%" stopColor="hsl(var(--destructive))" stopOpacity={0.2} />
                  </radialGradient>
                </defs>
                <PolarGrid stroke="hsl(var(--accent) / 0.3)" />
                <PolarAngleAxis 
                  dataKey="risk" 
                  tick={(props) => {
                    const { x, y, payload } = props;
                    const isActive = activeRisk === payload.value;
                    return (
                      <text
                        x={x}
                        y={y}
                        textAnchor="middle"
                        fill={isActive ? 'hsl(var(--accent))' : 'hsl(var(--foreground))'}
                        fontSize={isActive ? 14 : 12}
                        fontWeight={isActive ? 700 : 400}
                        style={{ transition: 'all 0.3s ease' }}
                      >
                        {payload.value}
                      </text>
                    );
                  }}
                />
                <PolarRadiusAxis angle={90} domain={[0, 100]} tick={false} />
                <Radar
                  dataKey="value"
                  stroke={activeRisk ? "hsl(var(--accent))" : "hsl(var(--destructive))"}
                  fill={activeRisk ? "hsl(var(--accent))" : "hsl(var(--destructive))"}
                  fillOpacity={activeRisk ? 0.35 : 0.15}
                  strokeWidth={activeRisk ? 4 : 2}
                  dot={{
                    r: 5,
                    fill: "hsl(var(--destructive))",
                    stroke: "hsl(var(--background))",
                    strokeWidth: 2
                  }}
                  activeDot={
                    activeRisk
                      ? {
                          r: 12,
                          fill: "hsl(var(--accent))",
                          stroke: "hsl(var(--background))",
                          strokeWidth: 3,
                          className: "animate-pulse-glow"
                        }
                      : undefined
                  }
                  className={activeRisk ? "animate-glow-chart" : ""}
                />
              </RadarChart>
            </ResponsiveContainer>
          </div>

          {/* Risk Cards - Always visible */}
          <div className="space-y-2.5 sm:space-y-3">
            {riskData.map((item, index) => {
              const Icon = item.icon;
              const isActive = activeRisk === item.risk;
              
              return (
                <div
                  key={item.risk}
                  role="button"
                  tabIndex={0}
                  aria-label={`Risiko ${item.risk}: ${item.value}% Bedrohungslevel. ${item.detail}`}
                  aria-pressed={isActive}
                  className={`
                    risk-card
                    bg-card/30 backdrop-blur-sm 
                    border-2 
                    rounded-xl p-4 sm:p-5 lg:p-6
                    cursor-pointer
                    transition-all duration-300 ease-out
                    ${isActive 
                      ? 'border-accent bg-card/60 scale-[1.02] shadow-[0_0_30px_hsl(var(--accent)/0.4)]' 
                      : 'border-border/50 hover:border-accent/50 hover:bg-card/50 hover:scale-[1.01]'
                    }
                    ${isVisible ? 'animate-fade-in' : 'opacity-0'}
                  `}
                  style={{ 
                    animationDelay: `${index * 100}ms`,
                    animationFillMode: 'forwards',
                    fontFamily: 'Manrope'
                  }}
                  onClick={() => handleRiskInteraction(item.risk)}
                  onMouseEnter={() => !isMobile && setActiveRisk(item.risk)}
                  onMouseLeave={() => !isMobile && setActiveRisk(null)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleRiskInteraction(item.risk);
                    }
                  }}
                >
                  <div className="flex items-start gap-3 sm:gap-4">
                    <div className={`
                      p-2.5 sm:p-3 rounded-xl 
                      border-2
                      transition-all duration-300
                      ${isActive 
                        ? 'bg-accent/20 border-accent/50 shadow-[0_0_20px_hsl(var(--accent)/0.3)]' 
                        : 'bg-destructive/10 border-destructive/20'
                      }
                    `}>
                      <Icon className={`
                        w-5 h-5 sm:w-6 sm:h-6 
                        transition-all duration-300
                        ${isActive ? 'text-accent scale-110' : 'text-destructive'}
                      `} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className={`
                        text-base sm:text-lg font-bold mb-1
                        transition-colors duration-300
                        ${isActive ? 'text-accent' : 'text-foreground'}
                      `}>
                        {item.risk}
                      </h3>
                      <p className="text-xs sm:text-sm text-muted-foreground">
                        {item.detail}
                      </p>
                    </div>
                    <div className="flex-shrink-0">
                      <div className={`
                        text-xl sm:text-2xl font-bold tabular-nums
                        transition-all duration-300
                        ${isActive ? 'text-accent scale-110' : 'text-destructive'}
                      `}>
                        {item.value}%
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default InteractiveRiskMap;
